import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// useParams
import { useParams } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { loadPlaylist } from "../../slices/mediaSlice";
import TimeBar from "./TimeBar";
import ChannelList from "./ChannelList";
import ProgramList from "./ProgramList";

const ChannelGuide = () => {
  const playlist = useSelector((state) => state.media.playlistData);
  const epgData = useSelector((state) => state.epg.epgData);
  const [channels, setChannels] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [windowStartTime, setWindowStartTime] = useState(null);
  const [windowEndTime, setWindowEndTime] = useState(null);
  const channelListRef = useRef(null);
  const programListRef = useRef(null);
  const { category } = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    if (playlist.length === 0) {
      dispatch(loadPlaylist({ mode: "live", group: category }));
    }
  }, [[]]);

  useEffect(() => {
    if (playlist.length > 0) {
      setChannels(playlist);
    }
  }, [playlist]);

  // Updated filter logic to include programs that start before or after the window, but overlap
  useEffect(() => {
    if (epgData.length > 0 && channels.length > 0) {
      const filtered = epgData.filter(
        (program) =>
          channels.some((channel) => channel.epg_channel_id === program.id) &&
          // Program starts before the window ends, and ends after the window starts
          program.start < windowEndTime &&
          program.stop > windowStartTime
      );
      setFilteredPrograms(filtered);
    }
    setLoading(false);
  }, [epgData, channels, windowStartTime, windowEndTime]);

  const handleBack = () => {
    setCurrentTime((prevTime) => prevTime - 60 * 60 * 1000); // Go back 1 hour
  };

  const handleForward = () => {
    setCurrentTime((prevTime) => prevTime + 60 * 60 * 1000); // Go forward 1 hour
  };

  window.handleBack = handleBack;
  window.handleForward = handleForward;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const generateTimeSlots = () => {
    const timeSlots = [];
    let current = new Date(currentTime);
    current.setMinutes(Math.floor(current.getMinutes() / 30) * 30, 0, 0); // Round down to nearest half hour

    // Create 6 time slots (for a 3-hour window: e.g., 12:00 PM, 12:30 PM, 1:00 PM, etc.)
    for (let i = 0; i < 6; i++) {
      timeSlots.push(new Date(current));
      current.setMinutes(current.getMinutes() + 30); // Add 30 minutes for each slot
    }

    // Set the window start and end times based on the generated time slots
    setWindowStartTime(timeSlots[0].getTime());
    setWindowEndTime(timeSlots[timeSlots.length - 1].getTime());

    return timeSlots;
  };

  useEffect(() => {
    generateTimeSlots(); // Generate time slots when component loads or currentTime changes
  }, [currentTime]);

  const onScroll = (scrollOffset) => {
    if (channelListRef.current && programListRef.current) {
      channelListRef.current.scrollTo(scrollOffset);
      programListRef.current.scrollTo(scrollOffset);
    }
  };

  if (loading) {
    return <> </>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        background: "#1c1c1c",
      }}
    >
      <TimeBar
        handleBack={handleBack}
        handleForward={handleForward}
        generateTimeSlots={generateTimeSlots}
        currentTime={currentTime}
        formatTime={formatTime}
        windowStartTime={windowStartTime} // Pass windowStartTime
        windowEndTime={windowEndTime} // Pass windowEndTime
      />
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <ChannelList
          channels={channels}
          channelListRef={channelListRef}
          onScroll={onScroll}
        />
        <ProgramList
          channels={channels}
          programListRef={programListRef}
          filteredPrograms={filteredPrograms}
          generateTimeSlots={generateTimeSlots}
          formatTime={formatTime}
          windowStartTime={windowStartTime} // Pass windowStartTime
          windowEndTime={windowEndTime} // Pass windowEndTime
          onScroll={onScroll}
        />
      </Box>
    </Box>
  );
};

export default ChannelGuide;
